import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import { CenterSpinner } from '@/components/ats';
import { useScheduleLogic } from '../../hooks/useScheduleLogic';
import { useMeetingRoomScheduleLogic } from '../../hooks/useMeetingRoomScheduleLogic';
import moment from 'moment-timezone';

import {
  SchCalToolbar,
  ManualSchCalHeader,
  SchEventContentRenderer,
  CandidateAvailabilityUpcomingPopover,
} from '../Components';

import '../SchCalStyles/SchCal.main.scss';
import { useCalendarEvents } from '../utils/useCalendarEvents';
import { useSchEventHandlers } from '../utils/schEventHandlers';
import { findTimeZone } from 'timezone-support';
import { KText } from '@/ui-library/components';

export const InterviewManualScheduler = ({
  calendarRef,
  interviewDataRef,
  registerForUpdates,
  updateSharedData,
  interviewPlan,
  jobId,
  isEdit,
  isBulkManual,
  jobStageActivityId,
  timezone: initialTimezone,
}) => {
  const { interviewers, bookedSlot, meetingRooms, datesSet } =
    interviewDataRef.current;
  const viewTitle = datesSet?.view?.getCurrentData()?.viewTitle || '';
  const currentWeekRange = viewTitle;
  const [timezone, setTimezone] = useState(
    datesSet?.timeZone && datesSet.timeZone !== 'local'
      ? datesSet.timeZone
      : findTimeZone(moment.tz.guess())?.name
  );

  const [localBookedSlot, setLocalBookedSlot] = useState(bookedSlot);

  const { isLoading, interviewersData, clearCache } = useScheduleLogic({
    interviewerIds: interviewers,
    interviewDataRef,
    jobId,
    currentWeekRange,
    registerForUpdates,
    updateSharedData,
    timeZone: timezone,
    getNextWeek: true,
    isBulkManual,
    jobStageActivityId,
  });

  const {
    isLoading: isMeetingRoomLoading,
    meetingRoomsData,
    clearCache: meetingRoomClearCache,
  } = useMeetingRoomScheduleLogic({
    meetingRoomIds: meetingRooms,
    interviewDataRef,
    jobId,
    currentWeekRange,
    registerForUpdates,
    updateSharedData,
    timeZone: timezone,
    getNextWeek: true,
    isBulkManual,
    jobStageActivityId,
  });

  const [, forceUpdate] = useState({});

  // Helper to calculate scroll time
  const getScrollTime = useCallback(
    (targetMoment) => {
      const tzMoment = targetMoment.tz(timezone);
      const startOfDay = tzMoment.clone().startOf('day');
      const endOfDay = startOfDay.clone().add(20, 'hours'); // 8 PM
      const startOfDayPlusTwo = startOfDay.clone().add(2, 'hours'); // 2 AM

      let scrollTime;
      if (tzMoment.isBetween(startOfDayPlusTwo, endOfDay)) {
        scrollTime = tzMoment.clone().subtract(2, 'hours'); // Show 2 hours before
      } else {
        scrollTime = tzMoment.clone(); // Scroll to exact time if outside range
      }
      return scrollTime.format('HH:mm:ss');
    },
    [timezone]
  );

  // Handle initial scroll on mount and when timezone changes
  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;

    if (localBookedSlot?.start) {
      // A booked slot exists (edit mode OR a pre-selected AI-recommended slot
      // in Schedule All create mode): navigate to that slot's week so it
      // stays visible even when candidate-availability slots would otherwise
      // pull the view to a different week.
      const bookedMoment = moment(localBookedSlot.start).tz(timezone);
      calendarApi.gotoDate(bookedMoment.toDate());
      calendarApi.scrollToTime(getScrollTime(bookedMoment));
    } else {
      const slots = interviewDataRef.current?.candidateAvailabilitySlots ?? [];
      const firstStart = slots[0]?.start_at;
      if (firstStart) {
        // Create mode with candidate availability: show first slot's week and scroll to it
        const firstSlotMoment = moment(firstStart).tz(timezone);
        calendarApi.gotoDate(firstSlotMoment.toDate());
        calendarApi.scrollToTime(getScrollTime(firstSlotMoment));
      } else {
        // Create mode (no slots): Scroll to current time
        const now = moment().tz(timezone);
        calendarApi.scrollToTime(getScrollTime(now));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarRef, isEdit, timezone, getScrollTime, localBookedSlot]);

  // When timezone changes, re-apply navigation/scroll
  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;

    if (localBookedSlot?.start) {
      const bookedMoment = moment(localBookedSlot.start).tz(timezone);
      calendarApi.gotoDate(bookedMoment.toDate());
      calendarApi.scrollToTime(getScrollTime(bookedMoment));
    } else {
      const slots = interviewDataRef.current?.candidateAvailabilitySlots ?? [];
      const firstStart = slots[0]?.start_at;
      if (firstStart) {
        const firstSlotMoment = moment(firstStart).tz(timezone);
        calendarApi.gotoDate(firstSlotMoment.toDate());
        calendarApi.scrollToTime(getScrollTime(firstSlotMoment));
      } else {
        const now = moment().tz(timezone);
        calendarApi.scrollToTime(getScrollTime(now));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timezone]);

  // Handle updates to bookedSlot or datesSet
  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;

    const unregisterBookedSlot = registerForUpdates('bookedSlot', (data) => {
      const { bookedSlot } = data?.fullData ?? {};
      setLocalBookedSlot(bookedSlot);
      forceUpdate({});
    });

    const unregisterDatesSet = registerForUpdates('datesSet', (_data) => {
      forceUpdate({});
    });

    return () => {
      unregisterBookedSlot();
      unregisterDatesSet();
    };
  }, [calendarRef, registerForUpdates, timezone, getScrollTime]);

  // Cleanup cache on unmount
  useEffect(() => {
    return () => {
      clearCache();
      meetingRoomClearCache();
    };
  }, [clearCache, meetingRoomClearCache]);

  const interviewerDataForCurrentWeek = useMemo(() => {
    const slice = interviewersData[currentWeekRange];
    return slice ? { ...slice } : {};
  }, [interviewersData, currentWeekRange]);

  const meetingRoomDataForCurrentWeek = useMemo(() => {
    const slice = meetingRoomsData[currentWeekRange];
    return slice ? { ...slice } : {};
  }, [currentWeekRange, meetingRoomsData]);

  const { events, hasAllDayEvent, hasCandidateAvailability } =
    useCalendarEvents({
      calendarRef,
      interviewDataRef,
      isLoading: isLoading || isMeetingRoomLoading,
      currentWeekRange,
      interviewerDataForCurrentWeek,
      meetingRoomDataForCurrentWeek,
      registerForUpdates,
      isEdit,
      interviewPlan,
      updateSharedData,
      timezone,
      isBulkManual,
    });

  // Event handlers
  const { handleSelect, handleEventResize, handleEventDrop } =
    useSchEventHandlers({
      bookedSlot,
      timezone,
      updateSharedData,
      calendarRef,
    });

  const [warningMessage, setWarningMessage] = useState(null);
  const [upcomingSlotsPopoverDismissed, setUpcomingSlotsPopoverDismissed] =
    useState(false);

  // When candidate has provided availability slots, show that week first instead of current week
  const initialDate = useMemo(() => {
    const slots = interviewDataRef.current?.candidateAvailabilitySlots ?? [];
    const firstStart = slots[0]?.start_at;
    return firstStart ? new Date(firstStart) : new Date();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- snapshot ref on mount only

  const hasSlotsInUpcomingWeeks = useMemo(() => {
    const slots = interviewDataRef.current?.candidateAvailabilitySlots ?? [];
    const visibleEnd = interviewDataRef.current?.datesSet?.end;
    if (!visibleEnd || !slots.length) return false;
    const end = moment(visibleEnd);
    return slots.some((slot) => moment(slot.start_at).isAfter(end));
  }, [interviewDataRef, currentWeekRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const showUpcomingSlotsPopover =
    hasSlotsInUpcomingWeeks && !upcomingSlotsPopoverDismissed;

  // Clear message after 3 seconds
  useEffect(() => {
    if (warningMessage) {
      const timer = setTimeout(() => setWarningMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [warningMessage]);

  return (
    <Flex flexDirection="column" h="100%" position="relative">
      {(isLoading || isMeetingRoomLoading) && (
        <Box
          position="absolute"
          top="0"
          left="0"
          width="100%"
          height="100%"
          bg="rgba(248, 249, 252, 0.4)"
          zIndex="10"
          display="flex"
          alignItems="center"
          justifyContent="center"
          cursor="not-allowed"
        >
          <CenterSpinner
            spinnerProps={{ color: '#797DFF', size: 'xl', speed: '1s' }}
          />
        </Box>
      )}
      <SchCalToolbar
        calendarRef={calendarRef}
        registerForUpdates={registerForUpdates}
        setTimezone={setTimezone}
        initialTimezone={initialTimezone}
      />
      <Box
        flex={1}
        minH={0}
        position="relative"
        w="100%"
        overflow="hidden"
        className={`k-interview-scheduler-calendar k-interview-manual-scheduler${hasCandidateAvailability ? ' has-candidate-availability' : ''}${isBulkManual ? ' is-bulk-manual' : ''}`}
      >
        <FullCalendar
          key={timezone}
          ref={calendarRef}
          plugins={[interactionPlugin, timeGridPlugin, momentTimezonePlugin]}
          nowIndicator={true}
          initialView="timeGridWeek"
          initialDate={initialDate}
          dayHeaderFormat={{ weekday: 'short', day: 'numeric' }}
          // @ts-expect-error ManualSchCalHeader props
          dayHeaderContent={(args) => <ManualSchCalHeader {...args} />}
          eventContent={(eventInfo) => (
            <SchEventContentRenderer
              eventInfo={eventInfo}
              registerForUpdates={registerForUpdates}
              interviewDataRef={interviewDataRef}
              updateSharedData={updateSharedData}
              onSelect={handleSelect}
              timezone={timezone}
            />
          )}
          eventOrderStrict={true}
          eventOrder="-extendedProps.priority,start_time"
          headerToolbar={false}
          selectable={true}
          selectMirror={false}
          dayMaxEvents
          weekends
          height="100%"
          allDaySlot={hasAllDayEvent}
          allDayContent={() => <KText variant="small_text">All day</KText>}
          slotDuration={'00:15:00'}
          snapDuration={'00:15:00'}
          slotLabelInterval={{ hours: 1 }}
          slotMinTime="00:00:00"
          slotMaxTime="24:00:00"
          expandRows={true}
          selectAllow={(selectInfo) => {
            const now = moment().tz(timezone);
            const duration = moment
              .tz(selectInfo.endStr, timezone)
              .diff(moment.tz(selectInfo.startStr, timezone), 'minutes');
            return selectInfo.start >= now.toDate() && duration <= 24 * 60;
          }}
          eventAllow={(dropInfo) =>
            dropInfo.start >= moment().tz(timezone).toDate()
          }
          timeZone={timezone}
          stickyHeaderDates={true}
          slotEventOverlap={true}
          select={handleSelect}
          eventResize={handleEventResize}
          eventDrop={handleEventDrop}
          slotLabelContent={({ text }) => (
            <KText variant="small_text" w="47px" alignItems="center">
              {text}
            </KText>
          )}
          slotLabelFormat={{
            hour: 'numeric',
            minute: '2-digit',
            omitZeroMinute: true,
            meridiem: true,
            separator: ' - ',
          }}
          datesSet={(arg) => {
            forceUpdate({});
            updateSharedData({
              datesSet: arg,
              currentWeek: (
                arg?.view as { getCurrentData?: () => { viewTitle?: string } }
              )?.getCurrentData?.()?.viewTitle,
            });
          }}
          events={events}
        />
        {showUpcomingSlotsPopover && (
          <CandidateAvailabilityUpcomingPopover
            isOpen={true}
            onDismiss={() => setUpcomingSlotsPopoverDismissed(true)}
            overlayOnly
          />
        )}
      </Box>
      {warningMessage && (
        <Box
          position="absolute"
          top="10px"
          left="50%"
          transform="translateX(-50%)"
          bg="yellow.100"
          color="yellow.800"
          p="2"
          borderRadius="md"
          boxShadow="md"
          zIndex="20"
          textAlign="center"
        >
          {warningMessage}
        </Box>
      )}
    </Flex>
  );
};
